import axios from "axios";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = `${BASE}/api`;

const client = axios.create({ baseURL: API, timeout: 20000 });

// Attach token and active workspace on every request
client.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("fb_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  const wsId = localStorage.getItem("fb_workspace_id");
  if (wsId) cfg.headers["X-Workspace-Id"] = wsId;
  return cfg;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    // If token expired/invalid, clear and bounce to sign-in
    if (err?.response?.status === 401 && localStorage.getItem("fb_token")) {
      localStorage.removeItem("fb_token");
      localStorage.removeItem("fb_workspace_id");
      if (!window.location.pathname.startsWith("/signin")) {
        window.location.href = "/signin";
      }
    }
    return Promise.reject(err);
  }
);

/** Trigger an authenticated CSV download without going through a plain <a href>.
 *  Pass `filename` to override the on-disk name; leave it undefined to honour the
 *  server's Content-Disposition. */
export async function downloadCsvViaApi(path, params = {}, filename) {
  const res = await client.get(path, { params, responseType: "blob" });
  const ctype = (res.headers?.["content-type"] || "").toLowerCase();
  if (!ctype.startsWith("text/csv") && !ctype.startsWith("application/csv")) {
    throw new Error("Server returned an unexpected response for CSV download");
  }
  // Honour server-provided filename when the caller didn't specify one
  let name = filename;
  if (!name) {
    const disp = res.headers?.["content-disposition"] || "";
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp);
    if (m) name = decodeURIComponent(m[1]);
  }
  name = name || "download.csv";
  const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default client;

/** Safely turn an Axios error into a display-ready string. FastAPI's default
 *  422 responses send `detail` as an array of Pydantic error dicts (not a
 *  string), so rendering it directly as a React child crashes the app.
 *  This normalises string | array-of-error-dicts | undefined → string. */
export function apiErrorMessage(ex, fallback = "Something went wrong.") {
  const detail = ex?.response?.data?.detail;
  if (!detail) return ex?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === "string" ? d : d?.msg)).filter(Boolean).join(", ") || fallback;
  }
  return fallback;
}

// Convenience
export const api = {
  // auth
  signup: (d) => client.post("/auth/signup", d).then((r) => r.data),
  signin: (d) => client.post("/auth/signin", d).then((r) => r.data),
  sso: (token) => client.post("/auth/sso", { token }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  verifyEmail: (token) => client.post("/auth/verify-email", { token }).then((r) => r.data),
  resendVerification: () => client.post("/auth/resend-verification").then((r) => r.data),
  patchMe: (d) => client.patch("/auth/me", d).then((r) => r.data),
  changeEmail: (d) => client.post("/auth/change-email", d).then((r) => r.data),
  cancelEmailChange: () => client.post("/auth/cancel-email-change").then((r) => r.data),
  requestPasswordReset: (email) => client.post("/auth/request-password-reset", { email }).then((r) => r.data),
  resetPassword: (token, new_password) => client.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
  deleteAccount: () => client.delete("/auth/account").then((r) => r.data),

  // workspaces
  createWorkspace: (d) => client.post("/workspaces", d).then((r) => r.data),
  workspaceDetail: (wid) => client.get(`/workspaces/${wid}`).then((r) => r.data),
  patchWorkspace: (wid, d) => client.patch(`/workspaces/${wid}`, d).then((r) => r.data),
  members: (wid) => client.get(`/workspaces/${wid}/members`).then((r) => r.data),
  removeMember: (wid, uid) => client.delete(`/workspaces/${wid}/members/${uid}`).then((r) => r.data),
  patchMember: (wid, uid, d) => client.patch(`/workspaces/${wid}/members/${uid}`, d).then((r) => r.data),
  patchMemberProfile: (wid, uid, d) => client.patch(`/workspaces/${wid}/members/${uid}/profile`, d).then((r) => r.data),
  patchMemberPermissions: (wid, uid, d) => client.patch(`/workspaces/${wid}/members/${uid}/permissions`, d).then((r) => r.data),
  uploadAvatar: (wid, uid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/workspaces/${wid}/members/${uid}/avatar`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  removeAvatar: (wid, uid) => client.delete(`/workspaces/${wid}/members/${uid}/avatar`).then((r) => r.data),
  memberRates: (wid, uid) => client.get(`/workspaces/${wid}/members/${uid}/rates`).then((r) => r.data),
  createMemberRate: (wid, uid, d) => client.post(`/workspaces/${wid}/members/${uid}/rates`, d).then((r) => r.data),
  updateMemberRate: (wid, uid, rid, d) => client.patch(`/workspaces/${wid}/members/${uid}/rates/${rid}`, d).then((r) => r.data),
  deleteMemberRate: (wid, uid, rid) => client.delete(`/workspaces/${wid}/members/${uid}/rates/${rid}`).then((r) => r.data),
  memberAssignments: (wid, uid) => client.get(`/workspaces/${wid}/members/${uid}/assignments`).then((r) => r.data),
  assignProject: (wid, uid, d) => client.post(`/workspaces/${wid}/members/${uid}/assignments`, d).then((r) => r.data),
  patchAssignment: (wid, uid, aid, d) => client.patch(`/workspaces/${wid}/members/${uid}/assignments/${aid}`, d).then((r) => r.data),
  unassignProject: (wid, uid, aid) => client.delete(`/workspaces/${wid}/members/${uid}/assignments/${aid}`).then((r) => r.data),
  patchAssignmentSettings: (wid, uid, d) => client.patch(`/workspaces/${wid}/members/${uid}/assignment-settings`, d).then((r) => r.data),
  bulkAssignAll: (wid, uid) => client.post(`/workspaces/${wid}/members/${uid}/assignments/bulk`).then((r) => r.data),
  bulkUnassignAll: (wid, uid) => client.delete(`/workspaces/${wid}/members/${uid}/assignments/bulk`).then((r) => r.data),
  roleTags: (include_archived = false) => client.get("/roles", { params: { include_archived } }).then((r) => r.data),
  createRoleTag: (d) => client.post("/roles", d).then((r) => r.data),
  updateRoleTag: (id, d) => client.patch(`/roles/${id}`, d).then((r) => r.data),
  deleteRoleTag: (id) => client.delete(`/roles/${id}`).then((r) => r.data),
  deptTags: (include_archived = false) => client.get("/departments", { params: { include_archived } }).then((r) => r.data),
  createDeptTag: (d) => client.post("/departments", d).then((r) => r.data),
  updateDeptTag: (id, d) => client.patch(`/departments/${id}`, d).then((r) => r.data),
  deleteDeptTag: (id) => client.delete(`/departments/${id}`).then((r) => r.data),
  invites: (wid) => client.get(`/workspaces/${wid}/invites`).then((r) => r.data),
  createInvite: (wid, d) => client.post(`/workspaces/${wid}/invites`, d).then((r) => r.data),
  revokeInvite: (wid, iid) => client.delete(`/workspaces/${wid}/invites/${iid}`).then((r) => r.data),
  inviteDetails: (token) => client.get(`/invites/${token}`).then((r) => r.data),
  acceptInvite: (token) => client.post(`/invites/${token}/accept`).then((r) => r.data),

  // projects & clients
  clients: () => client.get("/clients").then((r) => r.data),
  createClient: (d) => client.post("/clients", d).then((r) => r.data),
  updateClient: (id, d) => client.patch(`/clients/${id}`, d).then((r) => r.data),
  deleteClient: (id) => client.delete(`/clients/${id}`).then((r) => r.data),
  projects: (params = {}) => client.get("/projects", { params }).then((r) => r.data),
  createProject: (d) => client.post("/projects", d).then((r) => r.data),
  updateProject: (id, d) => client.patch(`/projects/${id}`, d).then((r) => r.data),
  deleteProject: (id) => client.delete(`/projects/${id}`).then((r) => r.data),
  archiveProject: (id) => client.post(`/projects/${id}/archive`).then((r) => r.data),
  unarchiveProject: (id) => client.post(`/projects/${id}/unarchive`).then((r) => r.data),
  pinProject: (id) => client.post(`/projects/${id}/pin`).then((r) => r.data),
  duplicateProject: (id) => client.post(`/projects/${id}/duplicate`).then((r) => r.data),
  projectDetail: (id) => client.get(`/projects/${id}/detail`).then((r) => r.data),
  nextProjectCode: () => client.get("/projects/next-code").then((r) => r.data),
  projectsExportUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${API}/projects/export.csv${qs ? `?${qs}` : ""}`;
  },

  // tasks (workspace library + per-project overrides)
  tasks: (includeArchived = false) => client.get("/tasks", { params: { include_archived: includeArchived } }).then((r) => r.data),
  createTask: (d) => client.post("/tasks", d).then((r) => r.data),
  updateTask: (id, d) => client.patch(`/tasks/${id}`, d).then((r) => r.data),
  deleteTask: (id) => client.delete(`/tasks/${id}`).then((r) => r.data),
  projectTasks: (pid) => client.get(`/projects/${pid}/tasks`).then((r) => r.data),
  attachProjectTask: (pid, d) => client.post(`/projects/${pid}/tasks`, d).then((r) => r.data),
  patchProjectTask: (pid, lid, d) => client.patch(`/projects/${pid}/tasks/${lid}`, d).then((r) => r.data),
  detachProjectTask: (pid, lid) => client.delete(`/projects/${pid}/tasks/${lid}`).then((r) => r.data),

  // rate cards
  rateCards: () => client.get("/rate-cards").then((r) => r.data),
  createRateCard: (d) => client.post("/rate-cards", d).then((r) => r.data),
  updateRateCard: (id, d) => client.patch(`/rate-cards/${id}`, d).then((r) => r.data),
  deleteRateCard: (id) => client.delete(`/rate-cards/${id}`).then((r) => r.data),

  // allocations (resource planning)
  allocations: (params) => client.get("/allocations", { params }).then((r) => r.data),
  upsertAllocation: (d) => client.post("/allocations", d).then((r) => r.data),
  deleteAllocation: (id) => client.delete(`/allocations/${id}`).then((r) => r.data),

  // Xero integration
  xeroStatus: () => client.get("/xero/status").then((r) => r.data),
  xeroConnect: () => client.post("/xero/connect").then((r) => r.data),
  xeroCallback: (d) => client.post("/xero/callback", d).then((r) => r.data),
  xeroDisconnect: () => client.post("/xero/disconnect").then((r) => r.data),
  xeroPushInvoice: (id) => client.post(`/xero/push-invoice/${id}`).then((r) => r.data),

  // platform (The Bench staff console)
  platformStats: () => client.get("/platform/stats").then((r) => r.data),
  platformWorkspaces: () => client.get("/platform/workspaces").then((r) => r.data),
  platformWorkspace: (id) => client.get(`/platform/workspaces/${id}`).then((r) => r.data),
  platformStaff: () => client.get("/platform/staff").then((r) => r.data),
  platformSearchUsers: (q) => client.get("/platform/users/search", { params: { q } }).then((r) => r.data),
  platformSetStaffRole: (id, role) => client.patch(`/platform/staff/${id}`, { platform_role: role }).then((r) => r.data),

  // billing (Stripe subscriptions)
  billingStatus: () => client.get("/billing/status").then((r) => r.data),
  billingCheckout: (lookup_key, quantity = 1) =>
    client.post("/billing/checkout", { lookup_key, quantity, origin_url: window.location.origin }).then((r) => r.data),
  billingCheckoutStatus: (sid) => client.get(`/billing/checkout/status/${sid}`).then((r) => r.data),
  billingPortal: () => client.post("/billing/portal", { origin_url: window.location.origin }).then((r) => r.data),
  billingInvoices: () => client.get("/billing/invoices").then((r) => r.data),
  billingUsage: () => client.get("/billing/usage").then((r) => r.data),
  billingChangePlan: (lookup_key, quantity = 1) =>
    client.post("/billing/subscription/change-plan", { lookup_key, quantity }).then((r) => r.data),
  billingCancel: () => client.post("/billing/subscription/cancel").then((r) => r.data),
  billingReactivate: () => client.post("/billing/subscription/reactivate").then((r) => r.data),

  // dashboard notifications
  dashboardNotifications: () => client.get("/dashboard/notifications").then((r) => r.data),

  // expenses
  expenses: (params = {}) => client.get("/expenses", { params }).then((r) => r.data),
  createExpense: (d) => client.post("/expenses", d).then((r) => r.data),
  updateExpense: (id, d) => client.patch(`/expenses/${id}`, d).then((r) => r.data),
  deleteExpense: (id) => client.delete(`/expenses/${id}`).then((r) => r.data),
  expenseCategories: (includeArchived = false) => client.get("/expense-categories", { params: { include_archived: includeArchived } }).then((r) => r.data),
  createExpenseCategory: (d) => client.post("/expense-categories", d).then((r) => r.data),
  updateExpenseCategory: (id, d) => client.patch(`/expense-categories/${id}`, d).then((r) => r.data),
  deleteExpenseCategory: (id) => client.delete(`/expense-categories/${id}`).then((r) => r.data),

  // time entries
  entries: (params) => client.get("/time-entries", { params }).then((r) => r.data),
  createEntry: (d) => client.post("/time-entries", d).then((r) => r.data),
  updateEntry: (id, d) => client.patch(`/time-entries/${id}`, d).then((r) => r.data),
  deleteEntry: (id) => client.delete(`/time-entries/${id}`).then((r) => r.data),
  reportSummary: (params) => client.get("/reports/summary", { params }).then((r) => r.data),
  reportTimeseries: (params) => client.get("/reports/timeseries", { params }).then((r) => r.data),

  // invoices
  invoicesList: () => client.get("/invoices").then((r) => r.data),
  createInvoice: (d) => client.post("/invoices", d).then((r) => r.data),
  updateInvoice: (id, d) => client.patch(`/invoices/${id}`, d).then((r) => r.data),
  deleteInvoice: (id) => client.delete(`/invoices/${id}`).then((r) => r.data),

  // imports
  importSchemas: () => client.get("/imports/schemas").then((r) => r.data),
  importPreview: (entity, csv_text) => client.post(`/imports/${entity}/preview`, { csv_text }).then((r) => r.data),
  importCommit: (entity, body) => client.post(`/imports/${entity}/commit`, body).then((r) => r.data),
  importTemplateUrl: (entity) => `${API}/imports/${entity}/template.csv`,
};
